import {
  FunctionComponent,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import { Check } from "@mui/icons-material";
import { Button, Dialog, DialogContent, IconButton } from "@material-ui/core";
import { Tenant } from "../../../models/Tenant";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import MaterialTable from "@material-table/core";
import * as TenantApi from "../../../api/tenantApi";
import { useI18n } from "../../../hooks/useI18n";
import { isOk } from "../../../result";
import { UserContext } from "../../../UserProvider";
import { Authenticated } from "../../../models/User";
import { put } from "../../../api/api";
import { Alert } from "@material-ui/lab";
import useStyles from "../styles";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";

const fetchTenants = async () => {
  const result = await TenantApi.list();
  if (isOk(result)) {
    return result.value;
  }

  throw new Error(`Failed to fetch tenant`);
};

export const addManagerToTenant = async (tenantId: string) => {
  const response = await put(`/v1/tenants/${tenantId}/manager`);

  if (response.ok) {
    return;
  }

  throw new Error(`Failed to add manager to tenant`);
};

interface TenantMenuProps {
  open: boolean;
  onClose: (reload: boolean) => void;
}

export const TenantMenu: FunctionComponent<TenantMenuProps> = ({
  open,
  onClose
}) => {
  const classes = useStyles();
  const { t } = useI18n();
  const [showNotification, setShowNotification] = useState(false);
  const [state, setState] = useState<ReadonlyArray<Tenant>>([]);

  const [selectedTenant, setSelectedTenant] = useState<Tenant>();
  const [temporarySelectedTenant, setTemporarySelectedTenant] =
    useState<Tenant>();
  const queryClient = useQueryClient();

  const user = useContext(UserContext) as Authenticated;

  const tenantsRef = useRef<Tenant[] | null>(null);

  const { isLoading } = useQuery(["tenants"], fetchTenants, {
    staleTime: 60000,
    cacheTime: 300000,
    refetchOnWindowFocus: false,
    onSuccess: data => {
      if (tenantsRef.current === data) return;
      tenantsRef.current = data;

      const selectedTenant = data.find(tenant => tenant.id === user.tenant.id);
      setSelectedTenant(selectedTenant);
      setTemporarySelectedTenant(selectedTenant);

      const otherTenants = data.filter(tenant => tenant !== selectedTenant);
      setState(selectedTenant ? [selectedTenant, ...otherTenants] : data);
    }
  });

  const { mutateAsync, isLoading: addManageToTenantLoading } = useMutation(
    addManagerToTenant,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["tenants"]);
      },
      onError: (err: Error | any) => {
        console.error("Selecting failed:", err);
      }
    }
  );

  return (
    <div>
      <Dialog
        fullWidth
        maxWidth={"sm"}
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle>
          {t("common.selectStore")}
          <IconButton
            aria-label={t("common.actions.close")}
            className={classes.closeButton}
            onClick={async () => {
              if (selectedTenant && temporarySelectedTenant) {
                if (temporarySelectedTenant.id !== selectedTenant.id) {
                  await mutateAsync(temporarySelectedTenant.id);
                  onClose(true);
                } else {
                  onClose(false);
                }
              } else {
                onClose(false);
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent dividers>
          <div>
            {showNotification &&
              temporarySelectedTenant?.id !== selectedTenant?.id && (
                <div>
                  <Alert
                    variant="filled"
                    severity="success"
                    style={{ margin: 0, width: "100%" }}
                  >
                    {t("common.messages.storeUpdated")}
                  </Alert>
                </div>
              )}
          </div>
          <MaterialTable
            isLoading={isLoading || addManageToTenantLoading}
            style={{ boxShadow: "none" }}
            title=""
            columns={[
              {
                title: t("common.formFields.name"),
                field: "name",
                render: (rowData: Tenant) => {
                  const customerId = rowData.customerId;
                  return customerId
                    ? `${customerId} - ${rowData.name}`
                    : rowData.name;
                },
                customFilterAndSearch: (term, rowData: Tenant) => {
                  const customerId = rowData.customerId;
                  const haystack = `${customerId ?? ""} - ${
                    rowData.name
                  }`.toLowerCase();
                  return haystack.includes((term ?? "").toLowerCase());
                }
              },
              {
                render: (rowData: Tenant) => (
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    {rowData.id === temporarySelectedTenant?.id ? (
                      <Button
                        startIcon={<Check />}
                        variant="outlined"
                        color="secondary"
                        size="small"
                        disabled
                        style={{ cursor: "not-allowed" }}
                      >
                        {t("common.actions.select")}
                      </Button>
                    ) : (
                      <Button
                        startIcon={<Check />}
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => {
                          setShowNotification(true);
                          setTemporarySelectedTenant(rowData);
                        }}
                        disabled={addManageToTenantLoading}
                      >
                        {t("common.actions.select")}
                      </Button>
                    )}
                  </div>
                ),
                cellStyle: { textAlign: "right" },
                headerStyle: { textAlign: "right" }
              }
            ]}
            data={state as Tenant[]}
            localization={{
              toolbar: {
                searchPlaceholder: t("common.search"),
                searchTooltip: t("common.searchTooltip")
              },
              pagination: {
                labelRowsSelect: t("rows"),
                labelDisplayedRows: t("displayedRowsTemplate"),
                firstTooltip: t("first"),
                previousTooltip: t("prev"),
                nextTooltip: t("next"),
                lastTooltip: t("last")
              },
              body: {
                emptyDataSourceMessage: t("noRecords")
              }
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};
